import { FC, ReactElement, useEffect, useState } from 'react';
import { t } from '@whitesharx/starfall-frontend-ttranslate';
import {
  HEADER_ID,
  NAVIGATION_BAR_ITEM_ID,
  PLAY_BUTTON_ID,
  useDrawer,
  DRAWER_ID,
  useVisualViewportResize,
} from '@whitesharx/starfall-frontend-core';
import { createPopup } from './utils';
import { highlightedStyles, headerStyles, barsHighlightedStyles, pulseBorderStyles, drawerStyles } from './styles.css';
import { BARS_ICON_ID } from '../InfoHeader/InfoHeader';
import { useEvent } from '~/src/hooks/useEvent';

type Props = {
  step: number;
  completeStep: () => void;
  setCanCompleteStep: (can: boolean) => void;
};

const MobileTutorial: FC<Props> = ({ step, completeStep, setCanCompleteStep }) => {
  const [content, setContent] = useState<ReactElement | null>(null);
  const { closeDrawer } = useDrawer();

  const drawFirstStep = () => {
    const header = document.getElementById(HEADER_ID);
    const bars = document.getElementById(BARS_ICON_ID);

    if (!bars || !header) {
      completeStep();
      return;
    }

    header.classList.add(headerStyles);
    bars.classList.add(barsHighlightedStyles, 'pulse', pulseBorderStyles);

    bars.addEventListener(
      'click',
      () => {
        header.classList.remove(headerStyles);
        bars.classList.remove(barsHighlightedStyles, 'pulse', pulseBorderStyles);

        // wait until drawer appears
        setTimeout(() => {
          const challenges = document.getElementById(NAVIGATION_BAR_ITEM_ID.challenges);
          const learn = document.getElementById(NAVIGATION_BAR_ITEM_ID.learn);
          const drawer = document.getElementById(DRAWER_ID);

          setCanCompleteStep(true);

          if (!challenges || !learn || !drawer) {
            completeStep();

            return;
          }

          drawer.classList.add(drawerStyles);
          challenges.classList.add(highlightedStyles);
          learn.classList.add(highlightedStyles);

          setContent(
            <>
              {createPopup(t('web-tutorial.challenges'), challenges, 'top', false)}
              {createPopup(t('web-tutorial.learn'), learn, 'top', false)}
            </>
          );
        }, 300);
      },
      { once: true }
    );
  };

  const drawSecondStep = () => {
    document.getElementById(NAVIGATION_BAR_ITEM_ID.challenges)?.classList.remove(highlightedStyles);
    document.getElementById(NAVIGATION_BAR_ITEM_ID.learn)?.classList.remove(highlightedStyles);
    const ranking = document.getElementById(NAVIGATION_BAR_ITEM_ID.ranking);

    if (!ranking) {
      completeStep();
      return;
    }

    ranking.classList.add(highlightedStyles);

    setContent(createPopup(t('web-tutorial.ranking'), ranking, 'top', false));
  };

  const drawThirdStep = () => {
    document.getElementById(NAVIGATION_BAR_ITEM_ID.ranking)?.classList.remove(highlightedStyles);
    const playNav = document.getElementById(NAVIGATION_BAR_ITEM_ID.play);

    if (!playNav) {
      return;
    }

    playNav.classList.add(highlightedStyles);

    setContent(createPopup(t('web-tutorial.play-nav'), playNav, 'top', false));

    document.getElementById('tutorial-root')!.addEventListener(
      'click',
      (e) => {
        e.preventDefault();

        closeDrawer();
        playNav.classList.remove(highlightedStyles);

        const playButton = document.getElementById(PLAY_BUTTON_ID);

        if (!playButton) {
          return;
        }

        playButton.style.zIndex = '8';
        setContent(createPopup(t('web-tutorial.play-button'), playButton, 'bottom'));
      },
      { once: true }
    );
  };

  const draw = useEvent(() => {
    if (step === 1) {
      drawFirstStep();
    }
    if (step === 2) {
      drawSecondStep();
    }
    if (step === 3) {
      drawThirdStep();
    }
  });

  useEffect(draw, [step]);
  useVisualViewportResize(draw);

  useEffect(() => {
    return () => {
      document.getElementById(HEADER_ID)?.classList.remove(headerStyles);
      document.getElementById(BARS_ICON_ID)?.classList.remove(barsHighlightedStyles, 'pulse', pulseBorderStyles);
      document.getElementById(DRAWER_ID)?.classList.remove(drawerStyles);
      document.getElementById(NAVIGATION_BAR_ITEM_ID.challenges)?.classList.remove(highlightedStyles);
      document.getElementById(NAVIGATION_BAR_ITEM_ID.learn)?.classList.remove(highlightedStyles);
      document.getElementById(NAVIGATION_BAR_ITEM_ID.ranking)?.classList.remove(highlightedStyles);
      document.getElementById(NAVIGATION_BAR_ITEM_ID.play)?.classList.remove(highlightedStyles);
    };
  }, []);

  return content;
};

export default MobileTutorial;
